import React, { useState, useEffect, useRef } from "react";

export const DVDStyleLoaderOverlay = ({
  containerRef,
  onClickBall,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
  onClickBall?: () => void;
}) => {
  const ballSize = 50;
  const [position, setPosition] = useState({ x: 100, y: 100 });
  const [isPaused] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [color, setColor] = useState(() => {
    // Random initial color
    return `#${Math.floor(Math.random() * 16777215).toString(16)}`;
  });

  // Use refs to avoid dependency issues in useEffect
  const getRandomVelocity = () => {
    const speed = 2.5; // Slower speed: 1.5 to 2.5
    return Math.random() > 0.5 ? speed : -speed; // Randomly make it positive or negative
  };

  const velocityRef = useRef({
    x: getRandomVelocity(),
    y: getRandomVelocity(),
  });

  const updateColor = () => {
    const randomColor = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
    setColor(randomColor);
  };

  useEffect(() => {
    const move = () => {
      if (!containerRef.current || isPaused) return;

      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;

      setPosition((prev) => {
        const newX = prev.x + velocityRef.current.x;
        const newY = prev.y + velocityRef.current.y;

        let newVelocityX = velocityRef.current.x;
        let newVelocityY = velocityRef.current.y;

        if (newX <= 0 || newX >= containerWidth - ballSize) {
          newVelocityX = -velocityRef.current.x;
          updateColor();
        }
        if (newY <= 0 || newY >= containerHeight - ballSize) {
          newVelocityY = -velocityRef.current.y;
          updateColor();
        }

        // Update velocity ref directly instead of using setState
        velocityRef.current = { x: newVelocityX, y: newVelocityY };

        return {
          x: Math.min(Math.max(newX, 0), containerWidth - ballSize),
          y: Math.min(Math.max(newY, 0), containerHeight - ballSize),
        };
      });
    };

    const interval = setInterval(move, 16); // ~60fps (16ms instead of 12ms)

    return () => clearInterval(interval);
  }, [isPaused, containerRef]); // Remove velocity from dependencies

  const handleClick = () => {
    setIsClicked(true);
    onClickBall?.();

    // Reset click state after animation
    setTimeout(() => {
      setIsClicked(false);
    }, 200);
  };

  return (
    <div
      style={{
        position: "absolute",
        top: position.y,
        left: position.x,
        width: ballSize,
        height: ballSize,
        backgroundColor: color,
        borderRadius: "50%",
        textAlign: "center",
        lineHeight: "50px",
        color: "white",
        fontWeight: "bold",
        boxShadow: isClicked
          ? "0 0 20px rgba(255,255,255,0.8), 0 2px 4px rgba(0,0,0,0.3)"
          : "0 2px 4px rgba(0,0,0,0.3)",
        cursor: "pointer",
        userSelect: "none",
        transform: isClicked ? "scale(1.2)" : "scale(1)",
        transition: "transform 0.1s ease-out, box-shadow 0.1s ease-out",
        zIndex: 10,
        pointerEvents: "auto",
      }}
      onClick={handleClick}
    ></div>
  );
};
